import { BaseActionObject, StateNodeConfig, assign } from "xstate4"
import isEqual from "lodash/isEqual"
import { getStudyPlansTransition } from "../../transitionalStates/getStudyPlansTransition"
import {
  Context,
  StudyPlanTargets,
  TeacherViewSchema,
  Transitions
} from "../../types"
import { collapsableSectionsState } from "../collapsableSections/collapsableSections"
import { breaksToggleState } from "../toggles/breaksToggle"
import { StorageLabel } from "../../../../../mp-utils/generic-utils"
import storage from "../../../../../mp-core/helpers/storage"
import {
  confirmRecommendedRestoreModal,
  openConfirmResetMasteryLesson,
  openConfirmRestoreMasteryLesson
} from "../modals"
import {
  addLessonDrawerState,
  lessonDetailsDrawerState,
  planSettingsDrawerState
} from "../drawers"
import { assignClassState } from "./states/assignClassState"
import {
  assignSubjectToClassesTransition,
  downloadResourcesTransition,
  getAssignedSubjectsByRefScopeTransition,
  getTeacherRelevantPlanInfoTransition,
  resolveStudentPlanConflictTransition,
  resetMasteryLessonTransition,
  restoreMasteryLessonTransition
} from "./transitionalStates"
import {
  togglePublishedPlanBreakAfterSectionTransition,
  togglePublishedPlanBreakBeforeSectionTransition,
  togglePublishedPlanBreakBeforeItemTransition,
  togglePublishedPlanBreakAfterItemTransition,
  togglePublishedTeachLiveTransition
} from "../toggles/transitionalStates"
import { getStudyPlanStorageKey } from "../../helpers"
import { setRedirectUrl } from "../../actions/setRedirectUrl"
import {
  selectSubjectAction,
  setSPV2RedirectUrl
} from "../../studyPlanV2/actions"
import { BASE_SP_V2_PATH } from "../../studyPlanV2/constants"

export const teacherView: StateNodeConfig<
  Context,
  TeacherViewSchema,
  Transitions,
  BaseActionObject
> = {
  id: "teacher_view",
  entry: [
    assign((ctx, evt, meta) => {
      // Check if we need to redirect BEFORE updating previousParentState
      const needsRedirect = ctx.previousParentState !== "teacher_view"
      const redirectResult = needsRedirect
        ? (ctx.isStudyPlanV2Enabled ? setSPV2RedirectUrl : setRedirectUrl)(
            ctx,
            evt,
            meta,
            StudyPlanTargets.TeacherView
          )
        : {}

      // Now update the context including previousParentState
      return {
        ...redirectResult,
        studyPlan: null,
        showDefaultInheritance: !Boolean(
          storage.get(
            getStudyPlanStorageKey(
              StorageLabel.SP_DEFAULT_INHERITANCE,
              ctx.user.id
            )
          )
        ),
        showMasterPlanInfo: !Boolean(
          storage.get(
            getStudyPlanStorageKey(
              StorageLabel.SP_MASTER_PLAN_INFO_VIEW,
              ctx.user.id
            )
          )
        ),
        currentTarget: StudyPlanTargets.TeacherView,
        previousParentState: "teacher_view",
        isReorderMode: false,
        expanded: ctx.publishedStudyPlan?.sections.map(({ id }) => id) || []
      }
    })
  ],

  initial: "loading",
  states: {
    loading: { ...getStudyPlansTransition },
    idle: {
      on: {
        /* Teacher interactions */
        SELECT_SUBJECT: [
          {
            /* get teacher plan by subject */
            target: "get_teacher_relevant_plan_info_transition",
            actions: assign((_ctx, evt) => ({
              subject: evt.newSubject
            })),
            cond: (ctx, evt) =>
              !ctx.isStudyPlanV2Enabled ||
              (ctx.isStudyPlanV2Enabled && !!evt.newSubject)
          },
          {
            target: `${BASE_SP_V2_PATH}.idle`,
            actions: assign(selectSubjectAction),
            cond: (ctx, evt) => ctx.isStudyPlanV2Enabled && !evt.newSubject
          }
        ],
        /* Study Plan V2 */
        SELECT_SP_V2_SUBPAGE: {
          target: `${BASE_SP_V2_PATH}.idle`,
          actions: assign((ctx, evt) =>
            setSPV2RedirectUrl(ctx, evt, null, evt.target)
          )
        },
        UPDATE_REPORTING_FILTERS_STATE: {
          target: "#StudyPlan.get_current_plan",
          actions: assign((ctx, event) => {
            const classId = event.state?.class?.[0]
            const studentId = event.state?.student?.[0]

            const classPlan = ctx.relevantClassPlansInfo.find(
              entity => entity.entityId === classId
            )
            const studentPlan = ctx.relevantStudentPlansInfo.find(
              entity => entity.entityId === studentId
            )

            const selectedEntity =
              classPlan || studentPlan
                ? !!studentPlan
                  ? studentPlan
                  : classPlan
                : null

            return {
              selectedEntity,
              isStudentPlan: !!studentPlan
            }
          }),
          cond: (ctx, event) => {
            const classId = event.state?.class?.[0]
            const studentId = event.state?.student?.[0]

            if (
              !isEqual(ctx.reportingFiltersState?.class, event.state?.class)
            ) {
              if (!classId) {
                return true
              }

              const classPlan = ctx.relevantClassPlansInfo.find(
                entity => entity.entityId === classId
              )

              return !!classPlan
            }

            if (
              !isEqual(ctx.reportingFiltersState?.student, event.state?.student)
            ) {
              if (!studentId) {
                return true
              }

              const studentPlan = ctx.relevantStudentPlansInfo.find(
                entity => entity.entityId === studentId
              )

              return !!studentPlan
            }

            return false
          }
        },
        /* ------ */

        FETCH_PLAN_BY_REF_SCOPE: {
          target: "#StudyPlan.get_current_plan",
          actions: assign((ctx, { entityId, scope }) => ({
            selectedEntity: entityId
              ? scope === "STUDENT"
                ? ctx.relevantStudentPlansInfo.find(
                    entity => entity.entityId === entityId
                  )
                : ctx.relevantClassPlansInfo.find(
                    entity => entity.entityId === entityId
                  )
              : null,
            isStudentPlan: scope === "STUDENT"
          }))
        },
        DISMISS_DEFAULT_INHERITANCE: {
          actions: [
            assign<Context>({
              showDefaultInheritance: false
            }),
            "rememberDefaultInheritanceDismiss"
          ]
        },
        DISMISS_MASTER_PLAN_INFO: {
          actions: [
            assign<Context>({
              showMasterPlanInfo: false
            }),
            "rememberDismissMasterPlanInfo"
          ]
        },
        BUILD_STUDY_PLAN: {
          /* Edit a published plan */
          target: "#StudyPlan.edit_published_plan_transition"
        },
        ADD_CLASS_PLAN: {
          target: "assign_class"
        },
        RESOLVE_STUDENT_CONFLICTS: {
          target: "resolve_student_plan_conflict"
        },
        PLAN_SETTINGS: {
          target: "plan_settings"
        },
        VIEW_RECOMMENDED_PLAN: {
          target: "#StudyPlan.get_recommended_plan_info_transition"
        },
        OPEN_VIEW_BREAK: {
          target: "#StudyPlan.view_break",
          actions: assign((_ctx, { sectionId, lessonId, breakType }) => ({
            selectedSectionId: sectionId,
            selectedLessonId: lessonId,
            selectedBreakType: breakType
          }))
        },
        OPEN_RESET_MASTERY_LESSON_CONFIRMATION: {
          target: "reset_mastery_lesson_confirmation",
          actions: assign((_ctx, evt) => ({
            selectedLessonId: evt.contentId,
            selectedSectionId: evt.sectionId
          }))
        },
        OPEN_RESTORE_MASTERY_LESSON_CONFIRMATION: {
          target: "restore_mastery_lesson_confirmation",
          actions: assign((_ctx, evt) => ({
            selectedLessonId: evt.contentId,
            selectedSectionId: evt.sectionId
          }))
        },
        TEACH_LIVE: {
          target: "toggle_published_teach_live_transition"
        },
        DOWNLOAD_RESOURCES: {
          target: "download_resources_transition",
          actions: assign((_ctx, evt) => ({
            selectedLessonId: evt.lessonId,
            selectedSectionId: evt.sectionId
          }))
        },
        REPORTS: {
          // target: "reports"
        },
        RESTORE_PLAN: {
          target: "restore_to_recommended_confirmation"
        },
        // TODO: all of these are in the menu bar, let's split and use it in both
        ...collapsableSectionsState.on,
        ...breaksToggleState.on,
        ADD_LESSON_TO_SECTION: {
          // TODO, rename to VIEW_SECTION_LESSON?
          target: "#StudyPlan.teacher_view.add_lesson",
          actions: assign((_ctx, { sectionId }) => ({
            selectedSectionId: sectionId
          }))
          // cond: (ctx) => !!ctx.studyPlan.adaptive
        },
        VIEW_DETAILS: {
          /* View details from published or draft plan */
          target: "#StudyPlan.teacher_view.lesson_detail_view",
          actions: assign((_ctx, { sectionId, lessonId }) => ({
            selectedSectionId: sectionId,
            selectedLessonId: lessonId,
            lessonDetailViewModal: true
          }))
        },
        /* Default review_plan  - events for any review_plan idle state */
        EDIT: {
          target: "#StudyPlan.edit_plan",
          cond: ctx => !!ctx.studyPlan
        }
      }
    },
    restore_to_recommended_confirmation: {
      ...confirmRecommendedRestoreModal
    },
    /* Drawers */
    add_lesson: { ...addLessonDrawerState },
    lesson_detail_view: { ...lessonDetailsDrawerState },
    plan_settings: { ...planSettingsDrawerState },
    /* Transitions */
    assign_class: { ...assignClassState },
    toggle_break_before_section_transition: {
      ...togglePublishedPlanBreakBeforeSectionTransition
    },
    toggle_break_after_section_transition: {
      ...togglePublishedPlanBreakAfterSectionTransition
    },
    toggle_break_before_lesson_transition: {
      ...togglePublishedPlanBreakBeforeItemTransition
    },
    toggle_break_after_lesson_transition: {
      ...togglePublishedPlanBreakAfterItemTransition
    },
    toggle_published_teach_live_transition: {
      ...togglePublishedTeachLiveTransition
    },
    download_resources_transition: { ...downloadResourcesTransition },
    assign_subject_to_classes_transition: {
      ...assignSubjectToClassesTransition
    },
    resolve_student_plan_conflict: { ...resolveStudentPlanConflictTransition },
    get_teacher_assigned_subjects_transition: {
      ...getAssignedSubjectsByRefScopeTransition
    },
    get_teacher_relevant_plan_info_transition: {
      ...getTeacherRelevantPlanInfoTransition
    },
    reset_mastery_lesson_confirmation: {
      ...openConfirmResetMasteryLesson
    },
    restore_mastery_lesson_confirmation: {
      ...openConfirmRestoreMasteryLesson
    },
    reset_mastery_lesson_transition: {
      ...resetMasteryLessonTransition
    },
    restore_mastery_lesson_transition: {
      ...restoreMasteryLessonTransition
    }
  },
  on: {
    /*
      These (RESET_MASTERY_LESSON and RESTORE_MASTERY_LESSON) are higher level events
      so we can acess them on the snackbar undo button
    */
    RESET_MASTERY_LESSON: {
      target: "#StudyPlan.teacher_view.reset_mastery_lesson_transition"
    },
    RESTORE_MASTERY_LESSON: {
      target: "#StudyPlan.teacher_view.restore_mastery_lesson_transition"
    }
  }
}
